"use client";

import {
  Box,
  Button,
  Field,
  Fieldset,
  Input,
  Textarea,
} from "@chakra-ui/react";
import {
  type PutTargetRequest,
  type PostTargetRequest,
  Target,
  SupportedLanguage,
} from "@/generated/fastapi";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, useWatch } from "react-hook-form";
import { useCallback, useEffect, useState } from "react";
import TOOLTIPS from "@/shared/config/tooltips";
import GenericDrawerInfo from "@/shared/ui/display/GenericDrawerInfo";
import GenericSegment from "@/shared/ui/controls/GenericSegment";
import {
  showErrorToast,
  showSuccessToast,
  showWarningToast,
} from "@/shared/feedback";
import GenericSelector from "@/shared/ui/controls/GenericSelector";
import GenericIcon from "@/shared/ui/actions/GenericIcon";
import {
  AudioTranscriptInputFlow,
  type AudioTranscriptInputMode,
} from "@/features/audio-input";

import useAutoDetectedLanguage from "@/shared/language/useAutoDetectedLanguage";
import {
  PROFILE_SHAPING_LABELS,
  PROFILE_SHAPING_MESSAGES,
} from "@/features/profile-shaping";
import {
  useCreateTargetMutation,
  useUpdateTargetMutation,
} from "@/features/targets";
import { useTranscribeAudioMutation } from "@/features/origins";

// Define validation schema
const SubmitTargetSchema = z.object({
  position: z
    .string()
    .min(2, "Focus title must be at least 2 characters")
    .max(100, "Focus title is too long")
    .trim(), // Trim whitespace

  context: z
    .string()
    .min(2, "Focus context must be at least 2 characters")
    .max(100, "Context is too long")
    .trim(),

  content: z
    .string()
    .min(10, "Focus material must be at least 10 characters")
    .max(10000, "Focus material is too long")
    .trim(),

  language: z.string().refine((val) => val !== "", {
    message: "Please select a language",
  }),
});

// Type inference for form data
type SubmitTargetFormData = z.infer<typeof SubmitTargetSchema>;
type TargetEntryMode = "text" | AudioTranscriptInputMode;

interface TargetSubmitFormProps {
  targetToEdit?: Target | null | undefined;
  onTargetSubmittedSuccess?: (target: Target) => void;
}

const ENTRY_MODE_LABELS: Record<TargetEntryMode, string> = {
  text: "Text input",
  voice: "Voice recording",
  audioFile: "Audio file",
};

export default function TargetSubmitForm({
  targetToEdit,
  onTargetSubmittedSuccess,
}: TargetSubmitFormProps) {
  const createTarget = useCreateTargetMutation();
  const updateTarget = useUpdateTargetMutation();
  const transcribeAudioMutation = useTranscribeAudioMutation();
  const [entryMode, setEntryMode] = useState<TargetEntryMode>("text");
  const [audioFlowResetKey, setAudioFlowResetKey] = useState(0);
  const [isInstantSubmitting, setIsInstantSubmitting] = useState(false);
  const isEditMode = Boolean(targetToEdit);
  const actionLabel = isEditMode
    ? PROFILE_SHAPING_LABELS.editFocus
    : PROFILE_SHAPING_LABELS.addFocus;

  // Setup form with proper default target values
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    clearErrors,
    getValues,
    trigger,
    control,
  } = useForm<SubmitTargetFormData>({
    resolver: zodResolver(SubmitTargetSchema),
    defaultValues: {
      position: targetToEdit?.position || "",
      context: targetToEdit?.context || "",
      content: targetToEdit?.content || "",
      language: targetToEdit?.language || "",
    },
  });

  const selectedLanguage = useWatch({
    control,
    name: "language",
  });
  const targetPosition = useWatch({
    control,
    name: "position",
    defaultValue: targetToEdit?.position || "",
  });
  const targetContext = useWatch({
    control,
    name: "context",
    defaultValue: targetToEdit?.context || "",
  });
  const targetContent = useWatch({
    control,
    name: "content",
    defaultValue: targetToEdit?.content || "",
  });
  const transcriptInputMode: AudioTranscriptInputMode | null =
    !isEditMode && (entryMode === "voice" || entryMode === "audioFile")
      ? entryMode
      : null;
  const isTranscriptMode = Boolean(transcriptInputMode);
  const activeModeLabel = isEditMode
    ? "Existing text"
    : ENTRY_MODE_LABELS[entryMode];
  const transcriptionMeta =
    transcribeAudioMutation.data?.transcription_meta ?? null;
  const canTranscribeAndSubmit =
    targetPosition.trim().length >= 2 &&
    targetContext.trim().length >= 2 &&
    Boolean(selectedLanguage);
  const transcribeAndSubmitDisabledReason = canTranscribeAndSubmit
    ? undefined
    : "Add a focus title, context, and language before saving from audio.";

  const handleDetectedLanguageChange = useCallback(
    (
      nextLanguage: SupportedLanguage | "",
      reason: "manual" | "auto" | "reset",
    ) => {
      setValue("language", nextLanguage, {
        shouldValidate: reason === "manual",
        shouldDirty: reason === "manual",
        shouldTouch: reason === "manual",
      });
    },
    [setValue],
  );

  const {
    languageItems,
    languageCodeToNameMap,
    detectedLanguage,
    autoDetectedLanguage,
    isDetectedLanguageSupported,
    selectedLanguages,
    handleLanguageSelection,
  } = useAutoDetectedLanguage({
    text: targetContent,
    selectedLanguage: selectedLanguage ?? "",
    onLanguageChange: handleDetectedLanguageChange,
  });

  const resetAudioDraftState = useCallback(() => {
    transcribeAudioMutation.reset();
    setValue("content", "", {
      shouldDirty: true,
      shouldValidate: false,
      shouldTouch: false,
    });
    clearErrors("content");
    setAudioFlowResetKey((current) => current + 1);
  }, [clearErrors, setValue, transcribeAudioMutation]);

  const handleEntryModeChange = useCallback(
    (nextMode: TargetEntryMode) => {
      setEntryMode(nextMode);
      resetAudioDraftState();
    },
    [resetAudioDraftState],
  );

  const handleTranscribe = useCallback(
    async (selectedFile: File) => {
      try {
        const result = await transcribeAudioMutation.mutateAsync({
          selectedFile,
          sourcedWhere: "studio",
        });

        setValue("content", result.transcribed_text ?? "", {
          shouldDirty: true,
          shouldValidate: true,
          shouldTouch: true,
        });
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Failed to transcribe audio";
        showErrorToast(message);
      }
    },
    [setValue, transcribeAudioMutation],
  );

  const resetNewTargetForm = useCallback(() => {
    setValue("position", "");
    setValue("context", "");
    setValue("content", "");
    setValue("language", "");
    setEntryMode("text");
    transcribeAudioMutation.reset();
    setAudioFlowResetKey((current) => current + 1);
  }, [setValue, transcribeAudioMutation]);

  const handleTargetSuccess = useCallback(
    (savedTarget: Target) => {
      if (!targetToEdit) {
        resetNewTargetForm();
      }

      onTargetSubmittedSuccess?.(savedTarget);
    },
    [onTargetSubmittedSuccess, resetNewTargetForm, targetToEdit],
  );

  const handleTranscribeAndSubmit = useCallback(
    async (selectedFile: File) => {
      const requiredFieldsReady = await trigger([
        "position",
        "context",
        "language",
      ]);

      if (!requiredFieldsReady) {
        return;
      }

      setIsInstantSubmitting(true);
      let transcribedText = "";

      try {
        const result = await transcribeAudioMutation.mutateAsync({
          selectedFile,
          sourcedWhere: "studio",
        });
        transcribedText = (result.transcribed_text ?? "").trim();

        if (transcribedText.length < 10) {
          showWarningToast("Focus material must be at least 10 characters");
          return;
        }

        const currentValues = getValues();
        const targetDataPost = {
          position: currentValues.position,
          context: currentValues.context,
          content: transcribedText,
          language: currentValues.language as SupportedLanguage,
        } as PostTargetRequest;
        const savedTarget = await createTarget.mutateAsync(targetDataPost);

        showSuccessToast("Focus lens saved");
        handleTargetSuccess(savedTarget as Target);
      } catch (error) {
        if (transcribedText) {
          setValue("content", transcribedText, {
            shouldDirty: true,
            shouldValidate: true,
            shouldTouch: true,
          });
        }

        showErrorToast(
          transcribedText
            ? "Failed to save focus lens"
            : error instanceof Error
              ? error.message
              : "Failed to transcribe audio",
        );
      } finally {
        setIsInstantSubmitting(false);
      }
    },
    [
      createTarget,
      getValues,
      handleTargetSuccess,
      setValue,
      transcribeAudioMutation,
      trigger,
    ],
  );

  // Update form when targetToEdit changes
  useEffect(() => {
    if (targetToEdit) {
      setValue("position", targetToEdit.position || "");
      setValue("context", targetToEdit.context || "");
      setValue("content", targetToEdit.content || "");
      if (targetToEdit.language) {
        setValue("language", targetToEdit.language);
      }
    } else {
      setValue("language", "");
    }
  }, [targetToEdit, setValue]);

  // Handle form submission
  const onSubmit = async (data: SubmitTargetFormData) => {
    // For updates, check if anything actually changed
    if (targetToEdit) {
      // Check if any field was modified
      const hasChanges =
        data.position !== targetToEdit.position ||
        data.context !== targetToEdit.context ||
        data.content !== targetToEdit.content ||
        data.language !== targetToEdit.language;

      // If nothing changed, show a message and close dialog
      if (!hasChanges) {
        showWarningToast("No focus changes detected");
        return;
      }
    }

    try {
      if (targetToEdit) {
        const targetDataPut = {
          id: targetToEdit.id,
          position: data.position,
          context: data.context,
          content: data.content,
          language: data.language as SupportedLanguage,
        } as PutTargetRequest;
        const savedTarget = await updateTarget.mutateAsync(targetDataPut);
        showSuccessToast("Focus lens updated");
        handleTargetSuccess(savedTarget as Target);
        return;
      }

      const targetDataPost = {
        position: data.position,
        context: data.context,
        content: data.content,
        language: data.language as SupportedLanguage,
      } as PostTargetRequest;
      const savedTarget = await createTarget.mutateAsync(targetDataPost);
      showSuccessToast("Focus lens saved");
      handleTargetSuccess(savedTarget as Target);
    } catch {
      showErrorToast(
        targetToEdit
          ? "Failed to update focus lens"
          : "Failed to save focus lens",
      );
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Fieldset.Root size="lg" invalid={Object.keys(errors).length > 0}>
        <Fieldset.Content>
          <GenericDrawerInfo
            description={PROFILE_SHAPING_MESSAGES.profileFocusLong}
            items={[
              { label: actionLabel, colorPalette: "blue" },
              ...(!isEditMode
                ? [{ label: activeModeLabel, colorPalette: "purple" as const }]
                : []),
              {
                label: selectedLanguage
                  ? (languageCodeToNameMap[selectedLanguage] ??
                    selectedLanguage)
                  : "Choose language",
                colorPalette: selectedLanguage ? "teal" : "gray",
              },
            ]}
          />

          <Field.Root invalid={!!errors.position}>
            <Field.Label>Focus title</Field.Label>
            <Input
              id="target-position"
              {...register("position")}
              placeholder="Job title, role, program, or opportunity"
            />

            <Field.HelperText whiteSpace="pre-line">
              {TOOLTIPS.Target.Position}
            </Field.HelperText>

            {errors.position && (
              <Field.ErrorText>{errors.position.message}</Field.ErrorText>
            )}
          </Field.Root>

          <Field.Root invalid={!!errors.context}>
            <Field.Label>Context</Field.Label>
            <Input
              id="target-context"
              {...register("context")}
              placeholder="Company, team, school, client, or project"
            />
            <Field.HelperText whiteSpace="pre-line">
              {TOOLTIPS.Target.Context}
            </Field.HelperText>
            {errors.context && (
              <Field.ErrorText>{errors.context.message}</Field.ErrorText>
            )}
          </Field.Root>

          {!isEditMode && (
            <Field.Root>
              <Field.Label>Input mode</Field.Label>
              <GenericSegment<TargetEntryMode>
                activeState={entryMode}
                setActiveState={handleEntryModeChange}
                options={[
                  { value: "text", label: "Text" },
                  { value: "voice", label: "Voice" },
                  { value: "audioFile", label: "Audio file" },
                ]}
                size="sm"
                disabled={isSubmitting}
              />
              <Field.HelperText>
                Choose whether to paste focus material or transcribe it first.
              </Field.HelperText>
            </Field.Root>
          )}

          {transcriptInputMode && (
            <AudioTranscriptInputFlow
              key={`${entryMode}-${audioFlowResetKey}`}
              canTranscribeAndSubmit={canTranscribeAndSubmit}
              errorMessage={transcribeAudioMutation.error?.message}
              isError={transcribeAudioMutation.isError}
              isTranscribing={
                transcribeAudioMutation.isPending || isInstantSubmitting
              }
              mode={transcriptInputMode}
              readyMessage="Transcript received. You can edit it below before saving the focus."
              transcribeAndSubmitDisabledReason={
                transcribeAndSubmitDisabledReason
              }
              transcribeAndSubmitLabel="Transcribe & save focus"
              transcribeAndSubmitLoadingText="Saving"
              transcriptValue={targetContent}
              transcriptionMeta={transcriptionMeta}
              onReset={resetAudioDraftState}
              onTranscribe={handleTranscribe}
              onTranscribeAndSubmit={handleTranscribeAndSubmit}
            />
          )}

          <Field.Root invalid={!!errors.content}>
            <Field.Label>Focus material</Field.Label>
            <Textarea
              autoresize
              minH="3xs"
              maxH="3xs"
              id="target-content"
              {...register("content")}
              placeholder={
                isTranscriptMode
                  ? "Your transcribed focus material will appear here for editing..."
                  : "Paste job post, role brief, program description, or opportunity notes..."
              }
            />
            <Field.HelperText>
              {isTranscriptMode
                ? "Review and edit the transcript before saving the focus."
                : "Paste the material this profile export should be ordered against."}
            </Field.HelperText>
            <Field.HelperText whiteSpace="pre-line">
              {TOOLTIPS.Target.Content}
            </Field.HelperText>

            {errors.content && (
              <Field.ErrorText>{errors.content.message}</Field.ErrorText>
            )}
          </Field.Root>

          <Field.Root invalid={!!errors.language}>
            <Field.Label>Language</Field.Label>
            <GenericSelector<SupportedLanguage>
              previewItems={languageItems}
              stateObject={selectedLanguages}
              stateObjectSetter={handleLanguageSelection}
              mode="select"
              placeholder="Select language"
              disabled={isSubmitting}
              allowMultiple={false}
              width="full"
            />
            {detectedLanguage && !isDetectedLanguageSupported && (
              <Box color="orange.500" fontSize="xs" mt="1">
                <GenericIcon icon="inform" size="xs" /> Unsupported detected
                language
              </Box>
            )}
            {autoDetectedLanguage &&
              isDetectedLanguageSupported &&
              selectedLanguage === autoDetectedLanguage && (
                <Box color="moveon.text.muted" fontSize="xs" mt="1">
                  Auto-detected:{" "}
                  {languageCodeToNameMap[autoDetectedLanguage] ??
                    autoDetectedLanguage}
                </Box>
              )}
            <Field.HelperText whiteSpace="pre-line">
              {TOOLTIPS.Language.general}
            </Field.HelperText>
            {errors.language && (
              <Field.ErrorText>{errors.language.message}</Field.ErrorText>
            )}
          </Field.Root>
        </Fieldset.Content>

        <Button
          bg="moveon.primary"
          type="submit"
          loading={
            isSubmitting || createTarget.isPending || updateTarget.isPending
          }
          loadingText="Saving focus"
          alignSelf="flex-end"
        >
          <GenericIcon icon={targetToEdit ? "edit" : "profile_focus"} />
          {targetToEdit
            ? PROFILE_SHAPING_LABELS.editFocus
            : PROFILE_SHAPING_LABELS.addFocus}
        </Button>
      </Fieldset.Root>
    </form>
  );
}
